import { ImageBlockSchema } from '@blocksuite/affine-model';
import {
  FetchUtils,
  FULL_FILE_PATH_KEY,
  getImageFullPath,
} from '@blocksuite/affine-shared/adapters';
import { getFilenameFromContentDisposition } from '@blocksuite/affine-shared/utils';
import { sha } from '@blocksuite/global/utils';
import {
  type AssetsManager,
  type ASTWalkerContext,
  type BlockSnapshot,
  nanoid,
} from '@blocksuite/store';

// 🔧 图片元数据序列化工具
interface ImageMetadata {
  caption?: string;
  originalUrl?: string;
}

/**
 * 将元数据序列化到 markdown alt 文本中
 * 格式: "{\"caption\": \"...\", \"originalUrl\": \"...\"}"
 */
export function serializeImageMetadata(metadata: ImageMetadata): string {
  if (!metadata.caption && !metadata.originalUrl) {
    return '';
  }
  const jsonStr = JSON.stringify(metadata);
  return jsonStr;
}

/**
 * 从 markdown alt 文本中解析元数据
 * 返回: { metadata: {...}, rawAlt: string }
 */
export function deserializeImageMetadata(alt: string | null): {
  metadata: ImageMetadata | null;
  rawAlt: string;
} {
  if (!alt) {
    return { metadata: null, rawAlt: '' };
  }

  try {
    // 从 alt 中移除 <meta> 标签
    const rawAlt = alt.trim();
    const metadata = JSON.parse(rawAlt) as ImageMetadata;

    return { metadata, rawAlt };
  } catch (error) {
    console.warn('Failed to deserialize image metadata:', error);
    return { metadata: null, rawAlt: alt };
  }
}

/**
 * 判断 URL 是否为有效的远程 URL（避免相对路径被当作 originalUrl）
 */
export function isValidRemoteUrl(url: string | undefined): boolean {
  if (!url) return false;
  
  // 排除相对路径
  if (url.startsWith('assets/') || url.startsWith('./') || url.startsWith('../')) {
    return false;
  }
  
  // 检查是否为绝对 URL
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export async function processImageNodeToBlock(
  imageURL: string,
  walkerContext: ASTWalkerContext<BlockSnapshot>,
  assets: AssetsManager,
  configs: Map<string, string>,
  template?: boolean | string,
  metadata?: { alt?: string | null; title?: string | null; originalUrl?: string }
) {
  let blobId = '';
  if (template) {
    // When template is true, the image URL is the full path of the image file
    blobId = imageURL;
  } else if (!FetchUtils.fetchable(imageURL)) {
    const fullFilePath = configs.get(FULL_FILE_PATH_KEY);
    // When importing markdown file with assets in a zip file,
    // the image URL is the relative path of the image file in the zip file
    // If the full file path is provided, use it to get the image full path
    if (fullFilePath) {
      const decodedImageURL = decodeURIComponent(imageURL);
      const imageFullPath = getImageFullPath(fullFilePath, decodedImageURL);
      blobId = assets.getPathBlobIdMap().get(imageFullPath) ?? '';
    } else {
      const imageURLSplit = imageURL.split('/');
      while (imageURLSplit.length > 0) {
        const key = assets
          .getPathBlobIdMap()
          .get(decodeURIComponent(imageURLSplit.join('/')));
        if (key) {
          blobId = key;
          break;
        }
        imageURLSplit.shift();
      }
    }
  } else {
    try {
      let blob: Blob | null = null;
      let fileName = '';

      // 🔥 策略 1：优先从 DOM 获取（避免重复下载）
      blob = await getImageFromDom(imageURL);
      if (blob) {
        console.log('✅ Image loaded from DOM (no network request needed)');
        fileName = (imageURL.split('/').at(-1) ?? 'image') + '.webp';
        blobId = await sha(await blob.arrayBuffer());
      } else {
        // 🔥 策略 2：DOM 中没有才通过网络下载
        console.log('⬇️ Image not in DOM, fetching from network...');
        const res = await FetchUtils.fetchImage(
          imageURL,
          undefined,
          configs.get('imageProxy') as string
        );
        
        if (!res) {
          console.error('Failed to fetch image from network');
          return;
        }

        const clonedRes = res.clone();
        fileName =
          getFilenameFromContentDisposition(
            res.headers.get('Content-Disposition') ?? ''
          ) ??
          (imageURL.split('/').at(-1) ?? 'image') +
            '.' +
            (res.headers.get('Content-Type')?.split('/').at(-1) ?? 'png');
        
        blob = await res.blob();
        blobId = await sha(await clonedRes.arrayBuffer());
      }

      if (!blob || !blobId) {
        console.error('Failed to process image: no blob or blobId');
        return;
      }

      const file = new File([blob], fileName, {
        type: blob.type || 'image/webp',
      });
      
      assets?.getAssets().set(blobId, file);
      await assets?.writeToBlob(blobId);
    } catch (err) {
      console.error('Failed to process image:', err);
      return;
    }
  }
  // 🔥 解析 alt 中可能包含的元数据
  let finalCaption = '';
  let finalOriginalUrl: string | undefined;

  // 先尝试从 alt 解析 <meta> 标签（处理 undefined 情况）
  const altString = metadata?.alt ?? null;
  const { metadata: parsedMeta } = deserializeImageMetadata(altString);
  
  if (parsedMeta) {
    // 🔥 从 <meta> 标签中提取元数据（LLM 返回的格式）
    finalCaption = parsedMeta.caption || '';
    finalOriginalUrl = parsedMeta.originalUrl;
  } else {
    // 🔥 普通情况：优先使用 title，fallback 到 alt
    finalCaption = metadata?.title || metadata?.alt || '';
    finalOriginalUrl = metadata?.originalUrl;
  }

  // 🔥 验证 originalUrl：只有真正的远程 URL 才保存，避免相对路径
  const validOriginalUrl = isValidRemoteUrl(finalOriginalUrl) ? finalOriginalUrl : undefined;

  walkerContext
    .openNode(
      {
        type: 'block',
        id: nanoid(),
        flavour: ImageBlockSchema.model.flavour,
        props: {
          sourceId: blobId,
          caption: finalCaption,
          originalUrl: validOriginalUrl,
        },
        children: [],
      },
      'children'
    )
    .closeNode();
  walkerContext.skipAllChildren();
}

// 修改 getImageFromDom 函数，使其更通用
async function getImageFromDom(url: string): Promise<Blob | null> {
  try {
    // 尝试从 DOM 中查找已加载的图片
    const img = document.querySelector<HTMLImageElement>(`img[src="${url}"]`);
    if (!img) {
      return null;
    }

    // 等待图片完全加载
    if (!img.complete) {
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        // 设置超时
        setTimeout(() => reject(new Error('Image load timeout')), 5000);
      });
    }

    // 检查图片尺寸是否有效
    if (img.naturalWidth === 0 || img.naturalHeight === 0) {
      return null;
    }

    // 🔥 关键修复：检测图片是否跨域
    // 如果图片跨域且没有 CORS 头，Canvas 会被污染，无法 toBlob
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    
    ctx.drawImage(img, 0, 0);

    // 🔥 尝试读取像素数据来检测是否被污染
    // 如果被污染，这一步会抛出 SecurityError
    try {
      ctx.getImageData(0, 0, 1, 1);
    } catch (securityError) {
      // Canvas 被污染，无法导出
      console.warn('Image is tainted (CORS issue), cannot export from DOM:', url);
      return null; // 返回 null，让后续逻辑走 fetch 下载
    }

    // 统一导出 WebP，quality 设为 0.9
    const blob = await new Promise<Blob | null>(resolve => 
      canvas.toBlob(resolve, 'image/webp', 0.9)
    );
    
    return blob;
  } catch (error) {
    console.warn('Failed to get image from dom:', error);
    return null; // 失败时返回 null，让后续走 fetch
  }
}
